import { useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  DEFAULT_OG_IMAGE,
  ROUTE_SEO,
  SITE_ORIGIN,
  absoluteUrl,
  organizationJsonLd,
  storeJsonLd,
  websiteJsonLd,
  type PageSeo,
} from '../../lib/seo';
import { getIndusVivaProduct } from '../../data/indusvivaProducts';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  if (!content) return;
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id: string, data: unknown) {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function resolvePageSeo(pathname: string, productId?: string | null): PageSeo {
  const base = ROUTE_SEO[pathname] || {
    title: 'iLeafs — Trusted Wellness Marketplace',
    description:
      'iLeafs — official IndusViva wellness & health products from trusted & guaranteed providers. India delivery.',
    path: pathname,
  };

  if (pathname === '/product' && productId) {
    const p = getIndusVivaProduct(productId);
    if (p) {
      return {
        ...base,
        title: `${p.name} — ${p.tagline} | iLeafs`,
        description: p.description.slice(0, 160),
        path: `/product?id=${p.id}`,
        type: 'product',
        image: absoluteUrl(p.image),
        keywords: `${p.name}, IndusViva, iLeafs, ${p.category}, wellness, health products India`,
      };
    }
  }
  return base;
}

/**
 * Client-side SEO / AEO / GEO head manager for the SPA.
 * Updates title, meta, canonical, Open Graph, Twitter, JSON-LD, and GA page_view.
 */
export function SeoHead() {
  const location = useLocation();
  const [params] = useSearchParams();

  useEffect(() => {
    const productId = params.get('id') || params.get('slug');
    const seo = resolvePageSeo(location.pathname, productId);
    const url = absoluteUrl(seo.path + (location.hash || ''));
    const image = seo.image || DEFAULT_OG_IMAGE;

    document.title = seo.title;
    document.documentElement.lang = 'en-IN';

    upsertMeta('name', 'description', seo.description);
    upsertMeta('name', 'keywords', seo.keywords || '');
    upsertMeta('name', 'robots', seo.noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1');
    upsertMeta('name', 'author', 'iLeafs');
    upsertMeta('name', 'theme-color', '#0e2417');
    upsertMeta('name', 'geo.region', 'IN');
    upsertMeta('name', 'geo.placename', 'India');
    upsertMeta('name', 'language', 'English');
    upsertMeta('name', 'rating', 'general');

    upsertLink('canonical', url);
    upsertLink('alternate', url);

    upsertMeta('property', 'og:type', seo.type === 'product' ? 'product' : 'website');
    upsertMeta('property', 'og:site_name', 'iLeafs');
    upsertMeta('property', 'og:locale', 'en_IN');
    upsertMeta('property', 'og:title', seo.title);
    upsertMeta('property', 'og:description', seo.description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', image);
    upsertMeta('property', 'og:image:alt', seo.title);

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', seo.title);
    upsertMeta('name', 'twitter:description', seo.description);
    upsertMeta('name', 'twitter:image', image);

    upsertJsonLd('ileafs-ld-org', organizationJsonLd());
    upsertJsonLd('ileafs-ld-website', websiteJsonLd());
    upsertJsonLd('ileafs-ld-store', storeJsonLd());

    if (location.pathname === '/product' && productId) {
      const p = getIndusVivaProduct(productId);
      if (p) {
        upsertJsonLd('ileafs-ld-product', {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: p.name,
          description: p.description,
          image: [absoluteUrl(p.image), absoluteUrl(p.originalImage)],
          sku: String(p.id),
          brand: { '@type': 'Brand', name: 'IndusViva' },
          category: p.category,
          offers: {
            '@type': 'Offer',
            url: absoluteUrl(`/product?id=${p.id}`),
            priceCurrency: 'INR',
            price: p.price.toFixed(2),
            availability: 'https://schema.org/InStock',
            itemCondition: 'https://schema.org/NewCondition',
            seller: { '@type': 'Organization', name: 'iLeafs' },
            areaServed: 'IN',
          },
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: String(p.rating),
            reviewCount: String(p.reviewCount),
          },
        });
      }
    } else {
      document.getElementById('ileafs-ld-product')?.remove();
    }

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_title: seo.title,
        page_location: url,
        page_path: `${location.pathname}${location.search}${location.hash}`,
        send_to: 'G-C6ZMXZ9Z8W',
      });
    }
  }, [location.pathname, location.search, location.hash, params]);

  return null;
}

/** Static FAQPage JSON-LD for AEO (answer engines). */
export function injectFaqJsonLd(
  sections: { title: string; items: { q: string; a: string }[] }[],
) {
  const mainEntity = sections.flatMap((sec) =>
    sec.items.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  );
  upsertJsonLd('ileafs-ld-faq', {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity,
    url: `${SITE_ORIGIN}/faq`,
    name: 'iLeafs Frequently Asked Questions',
  });
}
